// src/components/popups/StakeDetailsDrawer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './StakeDetailsDrawer.css';
import { getMyStakesForPost, getStakeSummaryForPost, listStakesByPost } from '../../api';
import { useTranslation } from 'react-i18next';

function fmt(n) { return (Number(n) || 0).toFixed(2); }
function fmtTime(ms) {
  if (ms <= 0) return 'Expired';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');

  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${pad(sec)}s`);
  return parts.join(' ');
}

/**
 * Props:
 * - isOpen, postId, onClose
 * - currentUser: string
 * - effectiveNetwork: 'mainnet' | 'testnet'
 */
export default function StakeDetailsDrawer({
  isOpen,
  postId,
  onClose,
  currentUser,
  effectiveNetwork = 'mainnet',
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // viewer-specific (used for header totals only)
  const [items, setItems] = useState([]);
  const [myTotals, setMyTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // post-wide
  const [postTotals, setPostTotals] = useState(null);
  const [globalActive, setGlobalActive] = useState([]);

  const [serverNow, setServerNow] = useState(() => new Date());
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !postId) return;
    setItems([]);
    setMyTotals(null);
    setPostTotals(null);
    setGlobalActive([]);
    setPage(1);
    setHasMore(true);
    prime(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, postId, currentUser, effectiveNetwork]);

  async function prime(firstPage) {
    try {
      setLoading(true);

      const [{ data: mine }, { data: postSum }, { data: global }] = await Promise.all([
        // your totals (auth)
        getMyStakesForPost(postId, {
          page: firstPage,
          limit: 20,
          network: effectiveNetwork,
        }).catch(() => ({
          data: { items: [], totals: null, hasMore: false, now: new Date().toISOString() }
        })),
        // totals (public)
        getStakeSummaryForPost(postId, {
          viewer: currentUser,
          network: effectiveNetwork,
        }),
        // all stakes (public)
        listStakesByPost(postId, { page: 1, limit: 50, network: effectiveNetwork }),
      ]);

      // viewer totals for header
      const arr = Array.isArray(mine?.items) ? mine.items : [];
      setItems(arr);
      setMyTotals(mine?.totals || null);
      setHasMore(!!mine?.hasMore);
      setPage(firstPage);
      setServerNow(mine?.now ? new Date(mine.now) : new Date());

      // post totals for header
      setPostTotals({
        activeTotalPi: Number(
          postSum?.stakeActivePi ??
          postSum?.totalActiveRefundable ??
          0
        ),
        activeCount: Array.isArray(postSum?.recentStakers) ? postSum.recentStakers.length : 0,
        nextExpiryAt: postSum?.stakeNextExpiryAt || null,
      });

      // global active (soonest expiry first)
      const now = new Date();
      const gItems = Array.isArray(global?.items) ? global.items : [];
      const filtered = gItems
        .filter(s =>
          s?.status === 'ACTIVE' &&
          s?.expiresAt && new Date(s.expiresAt) > now &&
          (s?.network || effectiveNetwork) === effectiveNetwork
        )
        .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
        .map(s => ({
          _id: s._id,
          amountPi: Number(s.amountPi || 0),
          refundablePi: Number(s.refundablePi || 0),
          expiresAt: s.expiresAt,
          status: s.status,
        }));
      setGlobalActive(filtered);
    } catch (e) {
      console.error('❌ Failed to load stake details/summary:', e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  const fetchPage = async (p) => {
    try {
      setLoading(true);
      const { data } = await getMyStakesForPost(postId, {
        page: p,
        limit: 20,
        network: effectiveNetwork,
      });
      const arr = Array.isArray(data?.items) ? data.items : [];
      setItems(prev => [...prev, ...arr]);
      setMyTotals(data?.totals || myTotals);
      setHasMore(!!data?.hasMore);
      setPage(p);
      setServerNow(data?.now ? new Date(data.now) : serverNow);
    } catch (e) {
      console.error('❌ Failed to load more stakes:', e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // live countdown (1s tick)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(t);
  }, [isOpen]);
  const now = useMemo(() => new Date(), [tick]);

  if (!isOpen) return null;

  const yourTotalPi = fmt(
    (Array.isArray(items) ? items : [])
      .reduce((sum, s) => {
        const active = s?.status === 'ACTIVE' && new Date(s?.expiresAt) > new Date();
        return active ? sum + Number(s?.amountPi || 0) : sum;
      }, 0)
  );

  const activeTotalPiAll = fmt(postTotals?.activeTotalPi ?? 0);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h3>{t('stake_detail.title')}</h3>
          <button className="drawer-close" onClick={onClose} aria-label={t('stake_detail.buttons.close')}>✕</button>
        </div>

        <div className="drawer-summary">
          <div className="summary-row">
            <span className="summary-label">{t('stake_detail.your_total')}</span>
            <span className="summary-value">{yourTotalPi} Pi</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">{t('stake_detail.active_total')}</span>
            <span className="summary-value">{activeTotalPiAll} Pi</span>
          </div>
        </div>

        <div className="drawer-list" ref={listRef}>
          {/* Only GLOBAL active stakes (no duplicate "Your active stakes" section) */}
          {globalActive.length > 0 && (
            <>
              <div className="drawer-section-title">{t('stake_detail.section_active_on_post')}</div>
              {globalActive.map((s) => {
                const exp = new Date(s.expiresAt);
                const remaining = exp.getTime() - now.getTime();
                const isActive = s.status === 'ACTIVE' && remaining > 0;
                return (
                  <div key={s._id} className="stake-row">
                    <div className="stake-row-main">
                      <div className="stake-amount">
                        <span className="label">{t('stake_detail.list_headers.amount')}</span>
                        <span className="value">{fmt(s.amountPi)} Pi</span>
                      </div>
                      <div className="stake-status">
                        <span className={`pill ${isActive ? 'active' : 'expired'}`}>
                          {isActive ? t('stake_detail.pills.active') : t('stake_detail.pills.expired')}
                        </span>
                      </div>
                    </div>
                    <div className="stake-row-sub">
                      <div>
                        <span className="label">{t('stake_detail.list_headers.refundable')}</span>
                        <span className="value">{fmt(s.refundablePi)} Pi</span>
                      </div>
                      <div>
                        <span className="label">{t('stake_detail.list_headers.expires')}</span>
                        <span className="value countdown">
                          {isActive ? t('stake_detail.in_time', { time: fmtTime(remaining) }) : exp.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Loading & empty states */}
          {globalActive.length === 0 && loading && (
            <div className="center"><div className="spinner" /></div>
          )}
          {globalActive.length === 0 && !loading && (
            <div className="empty">{t('stake_detail.empty.no_active')}</div>
          )}
          {loading && globalActive.length > 0 && (
            <div className="center"><div className="spinner" /></div>
          )}
        </div>
      </div>
    </div>
  );
}
